import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Provider } from "@/hooks/useProvidersQuery";

interface ProviderSelectorProps {
  providers: Provider[];
  selectedProvider: string;
  onProviderSelect: (providerId: string) => void;
}

export function ProviderSelector({ providers, selectedProvider, onProviderSelect }: ProviderSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter providers based on search term
  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    provider.specialty.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Select a Healthcare Provider</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by provider name or specialty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {filteredProviders.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            {searchTerm ? (
              <>
                <p className="font-medium">No providers found</p>
                <p className="text-sm">Try adjusting your search terms</p>
              </>
            ) : (
              <>
                <p className="font-medium">No providers available</p>
                <p className="text-sm">Please check back later or contact the clinic</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredProviders.map((provider) => (
            <Card
              key={provider.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedProvider === provider.id ? 'ring-2 ring-blue-600' : ''
              }`}
              onClick={() => onProviderSelect(provider.id)}
            >
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-lg">{provider.name}</h3>
                    <p className="text-blue-600">{provider.specialty}</p>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Experience: {provider.experience}</p>
                    <p>Rating: ⭐ {provider.rating}/5.0</p>
                    <p>Next Available: {provider.nextAvailable}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
